import React from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { AnimatedViews, useResponsive } from '@sg/garnish';

import { OrdersList } from '../../../OrdersList';

// ─────────────────────────────────────────────────────────────────────────────

export const OrderAgainLoadingPlaceholder = (props: OrderAgainLoadingPlaceholderProps) => {
  const { variant = 'orders' } = props;

  if (variant === 'favorites') {
    return (
      <OrdersList.FavoritesContainer>
        {FAVORITE_PLACEHOLDER_INDEXES.map((index) => (
          <FavoritePlaceholder key={index} index={index} />
        ))}
      </OrdersList.FavoritesContainer>
    );
  }

  return (
    <OrdersList.Container>
      {ORDER_PLACEHOLDER_INDEXES.map((index) => (
        <OrderPlaceholder key={index} index={index} />
      ))}
    </OrdersList.Container>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const OrderPlaceholder = (props: PlaceholderProps) => {
  const { index } = props;

  const { match } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const horizontalSpacingStyle = match([styles.horizontalSpacingXS, styles.horizontalSpacingSM]);
  const headerStyles = [styles.header, horizontalSpacingStyle];
  const lineItemStyles = [styles.lineItem, horizontalSpacingStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <OrdersList.OrderContainer>
      <View style={headerStyles}>
        <AnimatedViews.Pulse index={index} style={styles.headerContent}>
          <View style={styles.date} />
          <View style={styles.location} />
        </AnimatedViews.Pulse>
      </View>

      <OrdersList.OrderLineItemsContainer>
        <AnimatedViews.Pulse index={index} style={lineItemStyles}>
          <View style={styles.image} />

          <View style={styles.details}>
            <View style={styles.name} />
            <View style={styles.description} />
          </View>

          <View style={styles.reorderButton} />
        </AnimatedViews.Pulse>
      </OrdersList.OrderLineItemsContainer>
    </OrdersList.OrderContainer>
  );
};

const FavoritePlaceholder = (props: PlaceholderProps) => {
  const { index } = props;

  const { match } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const lineItemStyles = [
    styles.lineItem,
    match([styles.horizontalSpacingXS, styles.horizontalSpacingSM]),
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <AnimatedViews.Pulse index={index} style={lineItemStyles}>
      <View style={styles.image} />

      <View style={styles.details}>
        <View style={styles.name} />
        <View style={styles.description} />
      </View>

      <View style={styles.favoriteActions}>
        <View style={styles.reorderButton} />
        <View style={styles.favoriteButton} />
      </View>
    </AnimatedViews.Pulse>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ORDER_PLACEHOLDER_INDEXES = [...Array(4).keys()];
const FAVORITE_PLACEHOLDER_INDEXES = [...Array(6).keys()];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    paddingVertical: theme.spacing['4'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  headerContent: {
    flexDirection: 'row',
    columnGap: theme.spacing['2'],
  },
  horizontalSpacingXS: {
    paddingHorizontal: theme.spacing['4'],
  },
  horizontalSpacingSM: {
    paddingHorizontal: theme.spacing['6'],
  },
  date: {
    width: 62,
    height: 12,
    marginVertical: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  location: {
    width: 40,
    height: 12,
    marginVertical: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['2'],
  },
  image: {
    width: 80,
    height: 80,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  details: {
    flex: 1,
    rowGap: 4,
    marginRight: theme.spacing['2'],
  },
  name: {
    width: '72%',
    height: 16,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  description: {
    width: '52%',
    height: 12,
    marginVertical: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  reorderButton: {
    width: 32,
    height: 32,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  favoriteActions: {
    alignItems: 'center',
    rowGap: theme.spacing['3'],
  },
  favoriteButton: {
    width: 24,
    height: 24,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderAgainLoadingPlaceholderProps = {
  variant?: 'orders' | 'favorites';
};

type PlaceholderProps = {
  index: number;
};
